import axios from 'axios';
import { AuthResponse, LoginCredentials, RegisterData, User } from '@/types/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class AuthService {
  constructor() {
    this.setupAxios();
  }

  private setupAxios() {
    // Configure axios defaults
    axios.defaults.baseURL = API_URL;
    
    // Set token from localStorage if it exists
    const token = localStorage.getItem('token');
    if (token) {
      this.setToken(token);
    }
    
    // Add response interceptor for token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await axios.post('/auth/refresh', {
                refresh_token: refreshToken,
              });
              
              const { token } = response.data.data;
              localStorage.setItem('token', token);
              this.setToken(token);
              
              return axios(original);
            }
          } catch (refreshError) {
            this.removeToken();
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string): void {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  removeToken(): void {
    delete axios.defaults.headers.common['Authorization'];
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    console.log('🔐 Frontend: Login attempt for:', credentials.email);
    try {
      const response = await axios.post('/auth/login', credentials);
      console.log('✅ Frontend: Login successful:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ Frontend: Login failed:', error.response?.data || error.message);
      throw error;
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await axios.post('/auth/register', data);
    return response.data.data;
  }

  async logout(): Promise<void> {
    await axios.post('/auth/logout');
  }

  async getProfile(): Promise<User> {
    const response = await axios.get('/auth/profile');
    return response.data.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await axios.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async refreshToken(refreshToken: string): Promise<{ token: string; expires_in: number }> {
    const response = await axios.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data.data;
  }
}

export const authService = new AuthService();

// Initialize the service to set up axios interceptors
authService;